import { ArrowBack, ChevronRight, ExpandMore, Folder, MusicNote, PhotoLibrary, Refresh } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography, Button } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import util from '../../util';
import Image from '../../components/Image';
import FileViewer from '../../components/FileViewer';
import Dialog from '@mui/material/Dialog';

const IMAGE_PREVIEW_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "jfif", "avif", "apng"];
const VIDEO_PREVIEW_EXTENSIONS = ["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv", "m4v", "ogv"];
const AUDIO_PREVIEW_EXTENSIONS = ["mp3", "wav", "ogg", "oga", "m4a", "aac", "flac", "opus", "weba"];
const ROOT_ID = '__root__';
const PAGE_SIZE = 100;
const SCROLL_LOAD_THRESHOLD = 280;

function getGalleryFolderIdFromSearch(search = '') {
    try {
        return new URLSearchParams(search).get('folder') || ROOT_ID;
    } catch {
        return ROOT_ID;
    }
}

function buildGalleryUrl(folderId) {
    return folderId && folderId !== ROOT_ID
        ? `/contentManager/gallery?folder=${encodeURIComponent(folderId)}`
        : '/contentManager/gallery';
}

function getFileExtension(filename = "") {
    const originalName = filename.split("$Ğ!Ğ$")[0] || "";
    return originalName.split('.').pop()?.toLowerCase() || "";
}

function isMediaPreviewable(item = {}) {
    const extension = getFileExtension(item.filename);
    const mimetype = (item.mimetype || "").toLowerCase();

    return IMAGE_PREVIEW_EXTENSIONS.includes(extension)
        || VIDEO_PREVIEW_EXTENSIONS.includes(extension)
        || AUDIO_PREVIEW_EXTENSIONS.includes(extension)
        || mimetype.startsWith("image/")
        || mimetype.startsWith("video/")
        || mimetype.startsWith("audio/")
        || mimetype.includes("jfif");
}

function isVideoItem(item = {}) {
    const extension = getFileExtension(item.filename);
    const mimetype = (item.mimetype || "").toLowerCase();
    return VIDEO_PREVIEW_EXTENSIONS.includes(extension) || mimetype.startsWith("video/");
}

function isAudioItem(item = {}) {
    const extension = getFileExtension(item.filename);
    const mimetype = (item.mimetype || "").toLowerCase();
    return AUDIO_PREVIEW_EXTENSIONS.includes(extension) || mimetype.startsWith("audio/");
}

function displayName(filename = '') {
    return filename.split('$Ğ!Ğ$')[0] || filename;
}

function resolveFileUrl(value) {
    if (!value || typeof value !== 'string') return null;
    if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
        return value;
    }
    return `${util.GetFunctionRoot()}/file/${value}`;
}

function resolveChildPreviewSrc(folder = {}) {
  const childPreview = folder?.childPreview;
  if (!childPreview) return null;

  const base = util.GetFunctionRoot();
  const tokenParam = (folder.downloadToken ? `?token=${encodeURIComponent(folder.downloadToken)}` : "");

  function urlWithToken(value) {
    if (!value || typeof value !== 'string') return null;
    if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
      return value;
    }
    return `${base}/file/${value}${tokenParam}`;
  }

  if (typeof childPreview === 'string') {
    return urlWithToken(childPreview);
  }

  if (typeof childPreview === 'object') {
    return urlWithToken(childPreview.url)
      || urlWithToken(childPreview._id)
      || urlWithToken(childPreview.id)
      || null;
  }

  return null;
}

function toMappedEntries(data = [], parentId = null) {
    return data.map((file, i) => {
      const root = util.GetFunctionRoot();
      const tokenParam = file.downloadToken ? `?token=${encodeURIComponent(file.downloadToken)}` : "";

      const fileUrl = `${root}/file/${file._id}${tokenParam}`;
      const thumbUrl = (file.thumbnail
        ? `${root}/file/${file.thumbnail}${tokenParam}`
        : fileUrl);
      const largeThumbUrl = (file.largeThumbnail
        ? `${root}/file/${file.largeThumbnail}${tokenParam}`
        : thumbUrl);

      return {
          ...file,
          parent: parentId,
          url: fileUrl,
          thumbnail: thumbUrl,
          largeThumbnail: largeThumbUrl,
          index: i + 1,
      };
    });
}

function mergeUniqueById(existing = [], incoming = []) {
    const merged = [...existing, ...incoming];
    return merged.filter((item, index) => merged.findIndex((x) => x._id === item._id) === index);
}

const CDNGalleryPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user] = useState(JSON.parse(localStorage.getItem('user') || 'false') || false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [foldersByParent, setFoldersByParent] = useState({ [ROOT_ID]: [] });
    const [folderLookup, setFolderLookup] = useState({});
    const [mediaByFolder, setMediaByFolder] = useState({ [ROOT_ID]: [] });
    const [folderLoadState, setFolderLoadState] = useState({ [ROOT_ID]: 'idle' });
    const [folderPageState, setFolderPageState] = useState({ [ROOT_ID]: { offset: 0, hasMore: true, loadingMore: false } });
    const [expanded, setExpanded] = useState(new Set([ROOT_ID]));
    const initialFolderId = useRef(getGalleryFolderIdFromSearch(location.search)).current;
    const [selectedFolderId, setSelectedFolderId] = useState(initialFolderId);
    const lastUrlFolderRef = useRef(initialFolderId);
    const [contentLoading, setContentLoading] = useState(false);
    const [fileModalVisible, setFileModalVisible] = useState(false);
    const [fileModalData, setFileModalData] = useState({});
    const inFlightRef = useRef(new Map());

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        void loadTreeAndMedia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchFolderContent(parentId, offset = 0) {
        const apiParent = parentId === ROOT_ID ? null : parentId;
        const response = await util.postRequest('/api/content/myFiles', {
            parent: apiParent,
            offset,
            limit: PAGE_SIZE,
            sort: 0,
            order: -1,
            fullLoad: false,
        });

        const mapped = toMappedEntries(response.data || [], apiParent);
        const folders = mapped.filter((x) => x.type === 'folder');
        const media = mapped.filter((x) => x.type === 'file' && isMediaPreviewable(x));
        return {
            folders,
            media,
            fetchedCount: mapped.length,
            hasMore: !!response.hasMore,
        };
    }

    async function ensureFolderLoaded(folderId, force = false) {
        const key = folderId || ROOT_ID;
        const currentState = folderLoadState[key];
        if (!force && currentState === 'loaded') {
            return {
                folders: foldersByParent[key] || [],
                media: mediaByFolder[key] || [],
            };
        }

        if (!force && inFlightRef.current.has(key)) {
            return inFlightRef.current.get(key);
        }

        setFolderLoadState((prev) => ({ ...prev, [key]: 'loading' }));

        const request = (async () => {
            const { folders, media, fetchedCount, hasMore } = await fetchFolderContent(key, 0);

            setFoldersByParent((prev) => ({ ...prev, [key]: folders }));
            setMediaByFolder((prev) => ({ ...prev, [key]: media }));
            setFolderPageState((prev) => ({
                ...prev,
                [key]: {
                    offset: fetchedCount,
                    hasMore,
                    loadingMore: false,
                }
            }));
            setFolderLookup((prev) => {
                const next = { ...prev };
                for (const folder of folders) {
                    next[folder._id] = folder;
                }
                return next;
            });
            setFolderLoadState((prev) => {
                const next = { ...prev, [key]: 'loaded' };
                for (const folder of folders) {
                    if (!next[folder._id]) next[folder._id] = 'idle';
                }
                return next;
            });

            return { folders, media };
        })();

        inFlightRef.current.set(key, request);
        try {
            return await request;
        } catch (e) {
            setFolderLoadState((prev) => ({ ...prev, [key]: 'error' }));
            throw e;
        } finally {
            inFlightRef.current.delete(key);
        }
    }

    async function loadMoreForFolder(folderId) {
        const key = folderId || ROOT_ID;
        const pageState = folderPageState[key] || { offset: 0, hasMore: true, loadingMore: false };
        if (!pageState.hasMore || pageState.loadingMore) return;

        setFolderPageState((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || { offset: 0, hasMore: true }),
                loadingMore: true,
            }
        }));

        try {
            const { folders, media, fetchedCount, hasMore } = await fetchFolderContent(key, pageState.offset || 0);

            setFoldersByParent((prev) => ({
                ...prev,
                [key]: mergeUniqueById(prev[key] || [], folders),
            }));
            setMediaByFolder((prev) => ({
                ...prev,
                [key]: mergeUniqueById(prev[key] || [], media),
            }));
            setFolderLookup((prev) => {
                const next = { ...prev };
                for (const folder of folders) {
                    next[folder._id] = folder;
                }
                return next;
            });
            setFolderPageState((prev) => ({
                ...prev,
                [key]: {
                    offset: (pageState.offset || 0) + fetchedCount,
                    hasMore,
                    loadingMore: false,
                }
            }));
        } catch (e) {
            console.error(e);
            setFolderPageState((prev) => ({
                ...prev,
                [key]: {
                    ...(prev[key] || { offset: 0, hasMore: true }),
                    loadingMore: false,
                }
            }));
        }
    }

    async function loadTreeAndMedia() {
        setLoading(true);
        setError(false);

        try {
            const targetFolderId = getGalleryFolderIdFromSearch(location.search);
            inFlightRef.current.clear();
            setFoldersByParent({ [ROOT_ID]: [] });
            setMediaByFolder({ [ROOT_ID]: [] });
            setFolderLookup({});
            setFolderLoadState({ [ROOT_ID]: 'idle' });
            setFolderPageState({ [ROOT_ID]: { offset: 0, hasMore: true, loadingMore: false } });
            setSelectedFolderId(targetFolderId);
            setExpanded(new Set([ROOT_ID]));
            await ensureFolderLoaded(ROOT_ID, true);
            if (targetFolderId !== ROOT_ID) {
                await ensureFolderLoaded(targetFolderId, true);
            }
        } catch (e) {
            console.error(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const selectedMedia = useMemo(() => {
        const items = mediaByFolder[selectedFolderId] || [];
        return [...items].sort((a, b) => displayName(a.filename).localeCompare(displayName(b.filename)));
    }, [selectedFolderId, mediaByFolder]);

    function openFileViewer(item) {
        setFileModalData(item);
        setFileModalVisible(true);
        // Give the viewer its own history entry so back closes it instead of the page.
        navigate(buildGalleryUrl(selectedFolderId), { state: { galleryViewer: item._id } });
    }

    function closeFileViewer() {
        if (location.state?.galleryViewer) {
            navigate(-1);
            return;
        }
        setFileModalVisible(false);
        setFileModalData({});
    }

    useEffect(() => {
        const viewerId = location.state?.galleryViewer;
        if (viewerId) {
            if (fileModalVisible) return;
            const item = selectedMedia.find((f) => f._id === viewerId);
            if (item) {
                setFileModalData(item);
                setFileModalVisible(true);
            }
            return;
        }
        if (fileModalVisible) {
            setFileModalVisible(false);
            setFileModalData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, selectedMedia]);

    function expandPathToFolder(folderId) {
        setExpanded((prev) => {
            const next = new Set(prev);
            next.add(ROOT_ID);

            let cursor = folderId;
            let guard = 0;
            while (cursor && cursor !== ROOT_ID && guard < 500) {
                next.add(cursor);
                const parent = folderLookup[cursor]?.parent;
                cursor = parent || ROOT_ID;
                guard++;
            }

            return next;
        });
    }

    async function handleSelectFolder(folderId, updateUrl = true) {
        setSelectedFolderId(folderId);
        expandPathToFolder(folderId);
        setError(false);
        setContentLoading(true);

        if (updateUrl) {
            lastUrlFolderRef.current = folderId;
            if (getGalleryFolderIdFromSearch(location.search) !== folderId) {
                navigate(buildGalleryUrl(folderId));
            }
        }

        try {
            await ensureFolderLoaded(folderId);
        } catch (e) {
            console.error(e);
            setError(true);
        } finally {
            setContentLoading(false);
        }
    }

    // Browser back/forward moves between folders selected in the tree.
    useEffect(() => {
        const folderId = getGalleryFolderIdFromSearch(location.search);
        if (folderId === lastUrlFolderRef.current) return;
        lastUrlFolderRef.current = folderId;
        void handleSelectFolder(folderId, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    function toggleExpand(folderId) {
        let shouldLoad = false;
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
                shouldLoad = true;
            }
            return next;
        });

        if (shouldLoad) {
            void ensureFolderLoaded(folderId).catch((e) => {
                console.error(e);
            });
        }
    }

    function renderTreeNode(folder, depth = 0) {
        const nodeState = folderLoadState[folder._id] || 'idle';
        const children = foldersByParent[folder._id] || [];
        const hasChildren = children.length > 0;
        const hasKnownChildren = nodeState === 'loaded' ? hasChildren : true;
        const isExpanded = expanded.has(folder._id);
        const isSelected = selectedFolderId === folder._id;

        return (
            <Box key={folder._id}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: `${depth * 16}px`,
                        pr: 1,
                        py: 0.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#2f2f2f' : 'transparent',
                        '&:hover': { backgroundColor: '#252525' },
                    }}
                    onClick={() => {
                        void handleSelectFolder(folder._id);
                    }}
                >
                    <IconButton
                        size="small"
                        sx={{ mr: 0.5 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(folder._id);
                        }}
                    >
                        {nodeState === 'loading'
                            ? <CircularProgress size={14} />
                            : hasKnownChildren
                                ? (isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />)
                                : <ChevronRight sx={{ opacity: 0.2 }} fontSize="small" />}
                    </IconButton>
                    <Folder fontSize="small" sx={{ mr: 1, opacity: 0.85 }} />
                    <Typography noWrap sx={{ fontSize: 14 }}>
                        {displayName(folder.filename)}
                    </Typography>
                </Box>

                {hasChildren && isExpanded && children.map((child) => renderTreeNode(child, depth + 1))}
            </Box>
        );
    }

    const leftRootChildren = foldersByParent[ROOT_ID] || [];
    const selectedFolderName = selectedFolderId === ROOT_ID
        ? 'All folders'
        : displayName(folderLookup[selectedFolderId]?.filename || 'Folder');

    const currentFolders = foldersByParent[selectedFolderId] || [];
    const currentMedia = selectedMedia;
    const currentPageState = folderPageState[selectedFolderId] || { offset: 0, hasMore: false, loadingMore: false };
    const selectedParentId = selectedFolderId === ROOT_ID
        ? ROOT_ID
        : (folderLookup[selectedFolderId]?.parent || ROOT_ID);

    async function goUpFolder() {
        if (selectedFolderId === ROOT_ID) return;
        await handleSelectFolder(selectedParentId);
    }

    function handleContentScroll(event) {
        const el = event.currentTarget;
        const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceToBottom <= SCROLL_LOAD_THRESHOLD) {
            void loadMoreForFolder(selectedFolderId);
        }
    }

    return (
        <Box sx={{ mt: 2, height: 'calc(100vh - 100px)', backgroundColor: '#151515', borderRadius: 1, overflow: 'hidden' }}>
            <Dialog
                open={fileModalVisible}
                transitionDuration={0}
                onClose={closeFileViewer}
                sx={{ backgroundColor: '#00000000' }}
                PaperProps={{ style: { backgroundColor: '#00000000' } }}
                maxWidth
                fullScreen
            >
                <FileViewer
                    setFileModalData={setFileModalData}
                    fileModalData={fileModalData}
                    fileDatas={selectedMedia}
                    setFileModalVisible={setFileModalVisible}
                    onClose={closeFileViewer}
                />
            </Dialog>

            <Box sx={{ height: 44, px: 1, backgroundColor: '#202020', display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={() => navigate('/contentManager')}>
                    <ArrowBack />
                </IconButton>
                <PhotoLibrary sx={{ opacity: 0.9 }} />
                <Typography sx={{ fontWeight: 600 }}>Gallery</Typography>
                <Box sx={{ ml: 'auto' }}>
                    <Button size="small" startIcon={<Refresh />} onClick={loadTreeAndMedia}>Refresh</Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', height: 'calc(100% - 44px)' }}>
                <Box sx={{ width: 320, borderRight: '1px solid #2b2b2b', overflowY: 'auto', p: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            cursor: 'pointer',
                            backgroundColor: selectedFolderId === ROOT_ID ? '#2f2f2f' : 'transparent',
                            '&:hover': { backgroundColor: '#252525' },
                        }}
                        onClick={() => {
                            void handleSelectFolder(ROOT_ID);
                        }}
                    >
                        <ExpandMore fontSize="small" sx={{ mr: 0.5, opacity: 0.8 }} />
                        <Folder fontSize="small" sx={{ mr: 1, opacity: 0.85 }} />
                        <Typography noWrap sx={{ fontSize: 14 }}>All folders</Typography>
                    </Box>

                    {leftRootChildren.map((folder) => renderTreeNode(folder, 1))}
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }} onScroll={handleContentScroll}>
                    {loading || contentLoading ? (
                        <Box sx={{ mt: '25vh', textAlign: 'center' }}>
                            <CircularProgress size={48} />
                        </Box>
                    ) : error ? (
                        <Box sx={{ mt: '25vh', textAlign: 'center' }}>
                            <Typography color="error">Failed to load gallery data.</Typography>
                            <Button sx={{ mt: 1 }} onClick={loadTreeAndMedia}>Try again</Button>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ opacity: 0.9 }}>
                                    {selectedFolderName} • {currentFolders.length} folder{currentFolders.length === 1 ? '' : 's'} • {currentMedia.length} media item{currentMedia.length === 1 ? '' : 's'}
                                </Typography>
                                {selectedFolderId !== ROOT_ID && (
                                    <Button size="small" variant="outlined" onClick={goUpFolder}>Go up folder</Button>
                                )}
                            </Box>

                            {(currentFolders.length === 0 && currentMedia.length === 0) ? (
                                <Typography sx={{ opacity: 0.7, mt: 6, textAlign: 'center' }}>
                                    No folders or media found in this folder.
                                </Typography>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                        gap: 1.5,
                                    }}
                                >
                                    {currentFolders.map((folder) => (
                                        (() => {
                                            const childPreviewSrc = resolveChildPreviewSrc(folder);

                                            return <Box
                                            key={folder._id}
                                            sx={{
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                backgroundColor: '#1f1f1f',
                                                cursor: 'pointer',
                                                border: '1px solid #2a2a2a',
                                                '&:hover': { borderColor: '#565656' },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                            onClick={() => {
                                                void handleSelectFolder(folder._id);
                                            }}
                                        >
                                            <Box sx={{ width: '100%', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', backgroundColor: '#101010' }}>
                                                {childPreviewSrc ? (
                                                    <>
                                                        <Image
                                                            src={childPreviewSrc}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                right: 8,
                                                                bottom: 8,
                                                                backgroundColor: '#00000099',
                                                                borderRadius: '50%',
                                                                width: 30,
                                                                height: 30,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <Folder sx={{ fontSize: 18, opacity: 0.95 }} />
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <Folder sx={{ fontSize: 68, opacity: 0.85 }} />
                                                )}
                                            </Box>
                                            <Typography
                                                title={displayName(folder.filename)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.75,
                                                    fontSize: 12,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {displayName(folder.filename)}
                                            </Typography>
                                        </Box>
                                        })()
                                    ))}

                                    {currentMedia.map((item) => {
                                        const previewSrc = item.largeThumbnail || item.thumbnail || item.url;
                                        const video = isVideoItem(item);
                                        const audio = isAudioItem(item);

                                        return (
                                            <Box
                                                key={item._id}
                                                sx={{
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    backgroundColor: '#1f1f1f',
                                                    cursor: 'pointer',
                                                    border: '1px solid #2a2a2a',
                                                    '&:hover': { borderColor: '#565656' },
                                                }}
                                                onClick={() => openFileViewer(item)}
                                            >
                                                {video ? (
                                                    <video
                                                        src={item.url}
                                                        poster={previewSrc}
                                                        style={{ width: '100%', height: 180, objectFit: 'cover', backgroundColor: '#000' }}
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                    />
                                                ) : audio ? (
                                                    <Box sx={{ width: '100%', height: 180, backgroundColor: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                        <MusicNote sx={{ fontSize: 52, opacity: 0.8 }} />
                                                        <Typography sx={{ mt: 1, opacity: 0.75, fontSize: 12 }}>Audio</Typography>
                                                    </Box>
                                                ) : (
                                                    <Image
                                                        src={previewSrc}
                                                        item={item}
                                                        style={{ width: '100%', height: 180, objectFit: 'cover', backgroundColor: '#000' }}
                                                    />
                                                )}

                                                <Typography
                                                    title={displayName(item.filename)}
                                                    sx={{
                                                        px: 1,
                                                        py: 0.75,
                                                        fontSize: 12,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {displayName(item.filename)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            {currentPageState.loadingMore && (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <CircularProgress size={26} />
                                </Box>
                            )}

                            {!currentPageState.hasMore && (currentFolders.length > 0 || currentMedia.length > 0) && (
                                <Typography sx={{ opacity: 0.55, fontSize: 12, textAlign: 'center', py: 2 }}>
                                    Reached end of folder listing.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default CDNGalleryPage;